import { FC, useEffect } from 'react';
import { Form, DatePicker, Button, notification, Row, Col } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useAppSelector } from 'app/store';
import { useLazyReportsQuery } from 'app/services/edpApiV1/reports';
import { isDateRange } from 'shared/helpers/guard';
import { PICKER_DATE_MASK, SERVER_DATE_MASK } from 'shared/constants';
import dayjs from 'dayjs';
import { TOrganizationResponse } from 'app/services/edpApiV1/moderator';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import type { IFormRecordFormProps, IFormRecordProps, IFormRecordValues } from './types';
import OrganizationsInput from './organizationsInput/OrganizationsInput';

const FormRecord: FC<IFormRecordProps> = ({ type }) => {
  const [form] = Form.useForm<IFormRecordValues>();
  const currentOrganization = useAppSelector(state => state.entities.viewer.me.organization);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const [getReports, { isLoading, error }] = useLazyReportsQuery();

  const onFinish: IFormRecordFormProps['onFinish'] = data => {
    if (isDateRange(data.dateRange)) {
      const [fromDate, toDate] = data.dateRange.map(item => item.format(SERVER_DATE_MASK));
      const organizationIds = data.organizationIds?.length ? data.organizationIds : undefined;
      getReports({
        type,
        toDate,
        fromDate,
        organizationIds,
      }).then(() => form.resetFields());
    }
  };

  useEffect(() => {
    if (error != null) {
      notification.error({ message: Object.values(error).filter(Boolean).join(', ') });
    }
  }, [error]);

  useEffect(() => {
    if (currentOrganization.id || allAvailableOrganizations) {
      form.resetFields();
    }
  }, [currentOrganization.id, allAvailableOrganizations, form]);

  const onPickAll = (organizations: TOrganizationResponse[]) => {
    const organizationsIds: string[] = organizations
      .filter(({ id }) => id != null)
      .map(elem => {
        return elem.id;
      });
    form.setFieldValue('organizationIds', organizationsIds);
  };

  return (
    <Form form={form} layout="vertical" name="report-form" onFinish={onFinish}>
      <Row gutter={24}>
        <Col xs={24} md={8} lg={6}>
          <Form.Item name="dateRange">
            <DatePicker.RangePicker
              format={PICKER_DATE_MASK}
              style={{ width: '100%' }}
              disabledDate={current => current.isBefore(dayjs(0))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8} lg={6}>
          <OrganizationsInput onPickAll={onPickAll} isForViewer={true} />
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Form.Item dependencies={['dateRange']}>
            {({ getFieldValue }) => (
              <Button
                type="default"
                icon={<DownloadOutlined />}
                htmlType="submit"
                disabled={getFieldValue('dateRange') == null}
                loading={isLoading}
              >
                Скачать
              </Button>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export * from './types';
export default FormRecord;
